import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Layout from "@/pages/Layout";
import Landing from "@/pages/Landing";
import CookieBanner from "@/components/CookieBanner";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";

// Code-split heavy authenticated pages (dashboards, queue, settings) and the
// rarely-visited marketing/legal pages so the initial bundle stays small.
// Drops first-paint time on / and /login from ~6s to under 2s.
const Generate = lazy(() => import("@/pages/Generate"));
const Listicles = lazy(() => import("@/pages/Listicles"));
const ListicleDetail = lazy(() => import("@/pages/ListicleDetail"));
const Queue = lazy(() => import("@/pages/Queue"));
const Sites = lazy(() => import("@/pages/Sites"));
const Logs = lazy(() => import("@/pages/Logs"));
const Settings = lazy(() => import("@/pages/Settings"));
const Account = lazy(() => import("@/pages/Account"));
const Analytics = lazy(() => import("@/pages/Analytics"));
const RecipeDetail = lazy(() => import("@/pages/RecipeDetail"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Terms = lazy(() => import("@/pages/Terms"));
const Contact = lazy(() => import("@/pages/Contact"));
const GetStarted = lazy(() => import("@/pages/GetStarted"));
const Admin = lazy(() => import("@/pages/Admin"));
const FeaturePage = lazy(() => import("@/pages/FeaturePage"));
const GuidesIndex = lazy(() => import("@/pages/GuidesIndex"));
const GuideArticle = lazy(() => import("@/pages/GuideArticle"));
const PUBLIC_PAGES = ["wp-recipe-maker", "wp-delicious", "recipe-listicles", "recipefox-vs-chatgpt", "pricing", "faq"];

function ScrollToTop() {
    const { pathname, hash } = useLocation();
    useEffect(() => { if (!hash) window.scrollTo(0, 0); }, [pathname, hash]);
    return null;
}

const RouteFallback = () => (
    <div className="min-h-[60vh] flex items-center justify-center text-ink2 text-sm">
        Loading.
    </div>
);

function Private({ children }) {
    const { user, loading } = useAuth();
    if (loading) return <div className="min-h-screen flex items-center justify-center text-ink2">Loading.</div>;
    if (!user) return <Navigate to="/login" replace />;
    return children;
}

function Public({ children }) {
    const { user, loading } = useAuth();
    if (loading) return null;
    if (user) return <Navigate to="/generate" replace />;
    return children;
}

// SmartPublic: pages reachable by anyone, but when the user IS logged in
// we want them to keep their dashboard sidebar so they can navigate back.
function SmartPublic({ children }) {
    const { user, loading } = useAuth();
    if (loading) return null;
    if (user) return <Layout embed={children} />;
    return children;
}

function App() {
    return (
        <AuthProvider>
            <TooltipProvider delayDuration={200}>
                <BrowserRouter>
                <ScrollToTop />
                <Suspense fallback={<RouteFallback />}>
                <Routes>
                    <Route path="/" element={<Landing />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/contact" element={<SmartPublic><Contact /></SmartPublic>} />
                    {PUBLIC_PAGES.map(slug => <Route key={slug} path={`/${slug}`} element={<FeaturePage slug={slug} />} />)}
                    <Route path="/guides" element={<GuidesIndex />} />
                    <Route path="/guides/:slug" element={<GuideArticle />} />
                    <Route path="/get-started" element={<SmartPublic><GetStarted /></SmartPublic>} />
                    <Route path="/app" element={<Private><Layout /></Private>}>
                        <Route index element={<Navigate to="/generate" replace />} />
                    </Route>
                    <Route path="/" element={<Private><Layout /></Private>}>
                        <Route path="generate" element={<Generate />} />
                        <Route path="listicles" element={<Listicles />} />
                        <Route path="listicle/:id" element={<ListicleDetail />} />
                        <Route path="queue" element={<Queue />} />
                        <Route path="recipe/:id" element={<RecipeDetail />} />
                        <Route path="sites" element={<Sites />} />
                        <Route path="settings" element={<Settings />} />
                        <Route path="account" element={<Account />} />
                        <Route path="analytics" element={<Analytics />} />
                        <Route path="logs" element={<Logs />} />
                        <Route path="admin" element={<Admin />} />
                    </Route>
                </Routes>
                </Suspense>
                <CookieBanner />
                <Toaster position="top-right" richColors />
            </BrowserRouter>
            </TooltipProvider>
        </AuthProvider>
    );
}

export default App;
